import { BottomSheet, Button, Form, Typography, type FormValues, FormContext } from '@s4l/ds'
import { IconFilter } from '@s4l/icons/IconFilter'
import { IconTrash } from '@s4l/icons/IconTrash'
import { useS4LContext } from '@s4l/react-context'
import { SearchFilters } from '@s4l/stayforlong/property/domain/SearchFilters'
import { type UtilityClassNames } from '@s4l/utility-css'
import React, { useContext, useEffect, useRef } from 'react'
import styles from './index.module.css'

interface IFilterLayoutProps {
  children: React.ReactNode
  onSubmit?: (form: FormValues) => void
  onBottomSheetClosed?: () => void
}

export const FilterLayout = ({ children, onSubmit, onBottomSheetClosed }: IFilterLayoutProps) => {
  const { i18n } = useS4LContext()
  const [isOpen, setIsOpen] = React.useState(false)
  const formContext = useContext(FormContext)
  const filtersContentRef = useRef<HTMLDivElement>(null)

  // Scroll escape: when at top/bottom of filters, allow scroll to propagate to main page
  useEffect(() => {
    const element = filtersContentRef.current
    if (!element) return undefined

    const handleWheel = (event: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = element
      const isAtTop = scrollTop === 0
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

      const isScrollingUp = event.deltaY < 0
      const isScrollingDown = event.deltaY > 0

      // At boundaries scrolling outward: let event propagate to page
      if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
        return
      }

      // In the middle: capture scroll within the panel
      event.stopPropagation()
    }

    element.addEventListener('wheel', handleWheel, { passive: true })

    return () => {
      element.removeEventListener('wheel', handleWheel)
    }
  }, [])

  const onSubmitInternal = (form: FormValues) => {
    setIsOpen(false)
    onSubmit?.(form)
  }

  const onClearAll = () => {
    setIsOpen(false)
    onSubmit?.(SearchFilters.default().toPrimitives())
  }

  return (
    <>
      <BottomSheet
        open={isOpen}
        onClose={() => {
          onSubmitInternal(formContext.formFiltered)
          onBottomSheetClosed?.()
        }}
      >
        <BottomSheet.Trigger asChild>
          <Button
            className={['display-flex', 'desktop:display-none'].join(' ') as UtilityClassNames}
            variant="outlined-rounded"
            leadingIcon={<IconFilter size={16} />}
            data-testid="filter"
            size="small"
            fullWidth
            onClick={() => setIsOpen(true)}
          >
            <div className={styles.filtersHeader}>
              {i18n.formatMessage({ id: 'global.filters' })}
              <Form.CountBadge />
            </div>
          </Button>
        </BottomSheet.Trigger>
        <BottomSheet.Content
          scroll
          title={
            <div className={styles.filtersHeader}>
              {i18n.formatMessage({ id: 'global.filters' })}
              <Form.CountBadge size="medium" />
            </div>
          }
          footer={
            <>
              <Form.ClearButton
                data-testid="buttonReset"
                fullWidth
                hideTotalCount
                onClick={onClearAll}
              >
                {i18n.formatMessage({ id: 'global.clear-all' })}
              </Form.ClearButton>
              <Form.SubmitButton fullWidth onClick={onSubmitInternal}>
                {i18n.formatMessage({ id: 'button.apply' })}
              </Form.SubmitButton>
            </>
          }
        >
          {children}
        </BottomSheet.Content>
      </BottomSheet>
      <div className={styles.desktopFilters}>
        <div className={styles.filtersHeader}>
          <Typography as="h6" variant="title" size="xs">
            {i18n.formatMessage({ id: 'global.filters' })}
          </Typography>
          <Form.CountBadge size="medium" />
          <div className={styles.headerButtonReset}>
            <Form.ClearButton
              data-testid="headerButtonReset"
              small
              autoHide
              hideTotalCount
              icon={<IconTrash />}
            >
              {i18n.formatMessage({ id: 'global.clear-all' })}
            </Form.ClearButton>
          </div>
        </div>
        <div className={styles.filtersContent} ref={filtersContentRef}>
          {children}
        </div>
      </div>
    </>
  )
}

FilterLayout.displayName = 'SearchFilters.FilterLayout'
export default FilterLayout
